import React from 'react';
import './Page.css';
import usePageTitle from '../hooks/usePageTitle';

function StyleGuide() {
  usePageTitle('Style Guide');
  const colors = [
    { name: 'Midnight', hex: '#12233e', pantone: '533 C', usage: 'Navbar, headings, officer names, event date badges, footer background' },
    { name: 'Tide', hex: '#789e9c', pantone: '5503 C', usage: 'CTA sections, info borders, community event accents, raffle card border' },
    { name: 'Brick', hex: '#a02f21', pantone: '484 C', usage: 'Links, buttons, officer titles, event times, h1 accent underlines, step numbers' },
    { name: 'Parchment', hex: '#f7e8e0', pantone: '7604 C', usage: 'Event date badges, donation cards, form borders, info note backgrounds' },
    { name: 'White', hex: '#ffffff', pantone: '—', usage: 'Page backgrounds, cards, hero text' },
  ];

  const typography = [
    { element: 'Page Titles (h1)', font: 'Lora / Georgia, serif', weight: '400 (Regular)', size: '3.5rem', example: 'Our Lodge' },
    { element: 'Section Headings (h2)', font: 'Lora / Georgia, serif', weight: '400 (Regular)', size: '2.25rem', example: 'Lodge History' },
    { element: 'Card Titles (h3)', font: 'Proxima Nova / Montserrat, sans-serif', weight: '600 (Semibold), uppercase', size: '1.3rem', example: 'WORSHIPFUL MASTER' },
    { element: 'Body Text (p)', font: 'Proxima Nova / Montserrat, sans-serif', weight: '400 (Regular)', size: '1.05rem', example: 'Paragraph content' },
    { element: 'Links (a)', font: 'Inherits body font', weight: '—', size: '—', example: 'Brick color (#a02f21), darkens on hover' },
  ];

  return (
    <div className="page-container">
      <div className="page-hero our-lodge-hero" style={{ backgroundImage: 'url(/lodge-room.jpg)', backgroundPosition: 'center center' }}>
        <h1>Style Guide</h1>
        <p>Muncy Lodge No. 299 — Brand & Content Reference</p>
      </div>

      <div className="page-content">

        {/* --- INTRO --- */}
        <section className="content-section">
          <h2>About This Guide</h2>
          <p>
            This page documents the colors, typography, and component patterns used on our website. It serves as the reference for anyone updating site content — whether by hand, through GitHub, or with an AI coding agent. All styling follows the <a href="https://pagrandlodge.org/brand-center/" target="_blank" rel="noopener noreferrer">PA Grand Lodge Brand Center</a> guidelines.
          </p>
        </section>

        {/* --- COLORS --- */}
        <section className="content-section">
          <h2>Brand Colors</h2>
          <p>
            These colors come from the <a href="https://pagrandlodge.org/brand-center/" target="_blank" rel="noopener noreferrer">PA Grand Lodge Brand Center</a> and are defined in <code>src/theme.js</code>.
          </p>
          <div className="officers-grid">
            {colors.map((color) => (
              <div className="officer-card" key={color.name}>
                <div
                  style={{
                    backgroundColor: color.hex,
                    width: '100%',
                    height: '60px',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    border: color.hex === '#ffffff' ? '2px solid #f7e8e0' : 'none',
                  }}
                />
                <h3 style={{ color: '#a02f21' }}>{color.name}</h3>
                <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{color.hex}</p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>PANTONE: {color.pantone}</p>
                <p style={{ fontSize: '0.9rem' }}>{color.usage}</p>
              </div>
            ))}
          </div>
        </section>

        {/* --- TYPOGRAPHY --- */}
        <section className="content-section">
          <h2>Typography</h2>
          <p>
            Font choices follow the PA Grand Lodge style guide. Primary heading font is <strong>Lora</strong> (serif). Body and card titles use <strong>Proxima Nova</strong> with <strong>Montserrat</strong> as a web fallback. Defined in <code>src/theme.js</code> and <code>src/App.css</code>.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#12233e', color: '#ffffff' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Element</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Font</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Weight</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Size</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Example</th>
                </tr>
              </thead>
              <tbody>
                {typography.map((row, i) => (
                  <tr key={row.element} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f7e8e0' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>{row.element}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>{row.font}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>{row.weight}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>{row.size}</td>
                    <td style={{ padding: '0.75rem 1rem', fontStyle: 'italic' }}>{row.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ color: '#a02f21' }}>Live Examples</h3>
            <div className="officer-card" style={{ marginTop: '1rem' }}>
              <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 400, fontSize: '2.5rem', color: '#12233e', marginBottom: '0.5rem' }}>
                Page Title (h1)
              </h1>
              <h2 style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 400, fontSize: '2rem', color: '#12233e', marginBottom: '0.5rem' }}>
                Section Heading (h2)
              </h2>
              <h3 style={{ fontFamily: "'Montserrat', Arial, sans-serif", fontWeight: 600, fontSize: '1.3rem', color: '#a02f21', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                Card Title (h3)
              </h3>
              <p style={{ fontFamily: "'Montserrat', Arial, sans-serif", fontWeight: 400, fontSize: '1.05rem', color: '#333', lineHeight: 1.8 }}>
                Body text looks like this. It uses Proxima Nova / Montserrat at regular weight with comfortable line spacing.
              </p>
              <p>
                <a href="#colors" style={{ color: '#a02f21' }}>This is a link</a> — Brick color, darkens on hover.
              </p>
            </div>
          </div>
        </section>

        {/* --- COMPONENT PATTERNS --- */}
        <section className="content-section">
          <h2>Component Patterns</h2>
          <p>
            These are the reusable patterns used across all pages. Defined in <code>src/pages/Page.css</code>.
          </p>

          <h3 style={{ color: '#a02f21', marginTop: '2rem' }}>Cards</h3>
          <p>White background, 8px border radius, subtle shadow. Used for officers, events, contact items, store items, and FAQ.</p>
          <div className="officers-grid" style={{ marginTop: '1rem' }}>
            <div className="officer-card">
              <h3 style={{ color: '#a02f21' }}>Sample Card Title</h3>
              <p>Cards have a white background with <code>border-radius: 8px</code> and a subtle box-shadow using Midnight at 10% opacity.</p>
            </div>
            <div className="officer-card">
              <h3 style={{ color: '#a02f21' }}>Another Card</h3>
              <p>The grid uses <code>auto-fit, minmax(250px, 1fr)</code> so cards wrap responsively on any screen size.</p>
            </div>
          </div>

          <h3 style={{ color: '#a02f21', marginTop: '2rem' }}>Hero Sections</h3>
          <p>
            Each page has a hero banner with a background image, a 40% Midnight overlay, left-aligned white text, and a Brick accent underline on the h1. Defined with the <code>.page-hero</code> class.
          </p>

          <h3 style={{ color: '#a02f21', marginTop: '2rem' }}>Buttons</h3>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <a href="#buttons" className="cta-button primary" style={{ display: 'inline-block', padding: '0.75rem 1.5rem', backgroundColor: '#a02f21', color: '#f7e8e0', borderRadius: '4px', textDecoration: 'none', fontWeight: 600 }}>
              Primary Button (Brick)
            </a>
            <a href="#buttons" className="cta-button secondary" style={{ display: 'inline-block', padding: '0.75rem 1.5rem', backgroundColor: '#12233e', color: '#ffffff', borderRadius: '4px', textDecoration: 'none', fontWeight: 600 }}>
              Secondary Button (Midnight)
            </a>
          </div>

          <h3 style={{ color: '#a02f21', marginTop: '2rem' }}>Layout</h3>
          <ul>
            <li>Content max-width: <strong>1200px</strong>, centered</li>
            <li>Page content padding: <strong>5rem 3rem</strong> (desktop), <strong>2rem 1rem</strong> (mobile)</li>
            <li>Section spacing: <strong>5rem</strong> between sections</li>
            <li>Grid gap: <strong>1.5–2rem</strong> between cards</li>
          </ul>
        </section>

        {/* --- FILE REFERENCE --- */}
        <section className="content-section">
          <h2>Where Things Live in the Code</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#12233e', color: '#ffffff' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>What</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>File</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Officers & Lodge History', 'src/pages/OurLodge.js', 'Edit names/text directly in the JSX'],
                  ['Contact Info', 'src/pages/Contact.js', 'Email, phone, address, meeting times'],
                  ['Home Page Content', 'src/pages/Home.js', 'Hero, tenets, joining section'],
                  ['Store & Raffle', 'src/pages/Store.js', 'Deferred — do not modify yet'],
                  ['Calendar', 'Google Calendar', 'Managed via Google Calendar UI, not code'],
                  ['Brand Colors & Fonts', 'src/theme.js', 'Do not change without WM approval'],
                  ['Global Styles', 'src/App.css', 'Navbar, headings, links, base styles'],
                  ['Page Styles', 'src/pages/Page.css', 'Cards, grids, heroes, forms, store'],
                  ['AI Agent Guide', 'AGENTS.md', 'Guides AI tools on style and rules'],
                ].map(([what, file, notes], i) => (
                  <tr key={what} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f7e8e0' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>{what}</td>
                    <td style={{ padding: '0.75rem 1rem' }}><code>{file}</code></td>
                    <td style={{ padding: '0.75rem 1rem' }}>{notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* --- PA GRAND LODGE REFERENCES --- */}
        <section className="content-section">
          <h2>PA Grand Lodge Brand Center</h2>
          <p>
            Our site's branding follows the official Pennsylvania Grand Lodge guidelines. These are the authoritative sources for logos, colors, fonts, and content rules.
          </p>
          <div className="officers-grid">
            <a href="https://pagrandlodge.org/brand-center/" target="_blank" rel="noopener noreferrer" className="charity-link">
              <h3>Brand Center</h3>
              <p>Official logos, colors, fonts, and visual identity guidelines.</p>
            </a>
            <a href="https://pagrandlodge.org/brand-center/creating-a-lodge-website-content-branding/" target="_blank" rel="noopener noreferrer" className="charity-link">
              <h3>Content & Branding</h3>
              <p>Content guidelines, representation, privacy, and Masonic law compliance.</p>
            </a>
            <a href="https://pagrandlodge.org/brand-center/creating-a-lodge-website-setup/" target="_blank" rel="noopener noreferrer" className="charity-link">
              <h3>Website Setup</h3>
              <p>Technical guidance for lodge website configuration and hosting.</p>
            </a>
            <a href="https://pagrandlodge.org/wp-content/uploads/2024/01/PAMasons_StyleGuide-1.pdf" target="_blank" rel="noopener noreferrer" className="charity-link">
              <h3>Full Style Guide (PDF)</h3>
              <p>Complete PA Masons visual identity guide with Pantone values and typography rules.</p>
            </a>
          </div>
        </section>

        {/* --- CONTENT RULES --- */}
        <section className="content-section">
          <h2>Content Rules</h2>
          <p>
            Per the <a href="https://pagrandlodge.org/brand-center/creating-a-lodge-website-content-branding/" target="_blank" rel="noopener noreferrer">PA Grand Lodge Content & Branding Guidelines</a>, all lodge website content must follow these rules:
          </p>
          <div className="faq-list">
            <div className="faq-item">
              <h3>Worshipful Master Accountability</h3>
              <p>The Worshipful Master is accountable for all content posted on the lodge website. All changes require WM or Secretary approval.</p>
            </div>
            <div className="faq-item">
              <h3>No Lodge Notices</h3>
              <p>Lodge notices (trestle boards) must NOT be published on the public website. This is required by Masonic Law.</p>
            </div>
            <div className="faq-item">
              <h3>Privacy</h3>
              <p>Do not publish member names, addresses, or phone numbers without their explicit permission.</p>
            </div>
            <div className="faq-item">
              <h3>Grand Lodge Content</h3>
              <p>Do not duplicate Grand Lodge content — link to <a href="https://pagrandlodge.org" target="_blank" rel="noopener noreferrer">pagrandlodge.org</a> instead.</p>
            </div>
            <div className="faq-item">
              <h3>Credit Sources</h3>
              <p>All copied or quoted material must credit the original source.</p>
            </div>
            <div className="faq-item">
              <h3>External Links</h3>
              <p>External links must not conflict with the principles and tenets of the fraternity.</p>
            </div>
            <div className="faq-item">
              <h3>Lodge Identification</h3>
              <p>The lodge must be clearly identified as part of the Grand Lodge of Free and Accepted Masons of Pennsylvania, with a link to pagrandlodge.org.</p>
            </div>
            <div className="faq-item">
              <h3>Website Ownership</h3>
              <p>The lodge website is property of the lodge, not any individual. It must be transferable between officers.</p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

export default StyleGuide;
